'use client'

import { useEffect } from 'react'
import { createClient, isSupabaseConfigured } from '@/lib/supabase/client'
// CSS driver.js servi depuis /public/driver.css et chargé via <link> dynamique
// dans le useEffect, pour ne pas bloquer le rendu initial.

const DRIVER_CSS_HREF = '/driver.css'

function ensureDriverCssLoaded(): Promise<void> {
  return new Promise(resolve => {
    if (document.querySelector(`link[data-qualyse-driver-css]`)) return resolve()
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = DRIVER_CSS_HREF
    link.dataset.qualyseDriverCss = '1'
    link.onload = () => resolve()
    link.onerror = () => resolve() // ne bloque pas si la feuille a échoué
    document.head.appendChild(link)
  })
}

const STEP_ICONS = ['👋', '🧭', '📋', '🎙', '🏷', '📊', '💳', '💬', '🚀']
const TOTAL = 9

export default function OnboardingTour() {
  useEffect(() => {
    if (!isSupabaseConfigured) return
    const supabase = createClient()
    if (!supabase) return

    const run = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', user.id)
        .maybeSingle()

      if (profile?.onboarding_completed) return

      const markComplete = async () => {
        await supabase
          .from('profiles')
          .update({ onboarding_completed: true })
          .eq('id', user.id)
      }

      await new Promise(r => setTimeout(r, 1200))

      // Charge la lib JS + sa feuille de style en parallèle (CSS lazy via <link>
      // depuis /public/driver.css = pas bloquant au rendu initial de la page)
      const [{ driver }] = await Promise.all([
        import('driver.js'),
        ensureDriverCssLoaded(),
      ])

      // Ref mutable pour éviter la référence circulaire dans onPopoverRender
      const destroyRef: { fn: () => void } = { fn: () => {} }

      const driverObj = driver({
        showProgress: false,
        nextBtnText: 'Suivant →',
        prevBtnText: '← Précédent',
        doneBtnText: "C'est parti !",
        allowClose: true,
        overlayColor: 'rgba(0,0,0,0.5)',
        popoverClass: 'qualyse-tour',
        stagePadding: 8,
        stageRadius: 6,
        onDestroyed: markComplete,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onPopoverRender: (popover: any, { state }: { state: { activeIndex?: number } }) => {
          const idx = state.activeIndex ?? 0
          const current = idx + 1
          const icon = STEP_ICONS[idx] ?? '✨'
          const wrapper = popover.wrapper as HTMLElement

          // ── Header : badge emoji + label VISITE GUIDÉE ──────────
          let header = wrapper.querySelector<HTMLElement>('.qts-header')
          if (!header) {
            header = document.createElement('div')
            header.className = 'qts-header'
            wrapper.insertBefore(header, wrapper.firstChild)
          }
          header.innerHTML =
            `<div class="qts-badge">${icon}</div>` +
            `<span class="qts-label">VISITE GUIDÉE</span>`

          // ── Pied : tirets de progression + "Passer la visite" ──
          let foot = wrapper.querySelector<HTMLElement>('.qts-foot')
          if (!foot) {
            foot = document.createElement('div')
            foot.className = 'qts-foot'
            wrapper.appendChild(foot)
            // Événement délégué — listener unique, survit aux innerHTML updates
            foot.addEventListener('click', (e) => {
              if ((e.target as HTMLElement).classList.contains('qts-skip')) {
                e.preventDefault()
                destroyRef.fn()
              }
            })
          }

          const dashes = Array.from({ length: TOTAL }, (_, i) =>
            `<div class="qts-dash${i + 1 === current ? ' on' : ''}"></div>`
          ).join('')
          const s = String(current).padStart(2, '0')
          const t = String(TOTAL).padStart(2, '0')

          foot.innerHTML =
            `<div class="qts-prog">` +
              `<div class="qts-dashes">${dashes}</div>` +
              `<span class="qts-step">Étape ${s} / ${t}</span>` +
            `</div>` +
            `<a href="#" class="qts-skip">Passer la visite</a>`
        },
        steps: [
          {
            popover: {
              title: 'Bienvenue sur Qualyse ! 👋',
              description:
                "En quelques secondes, on vous montre comment tout fonctionne. C'est simple, promis !",
            },
          },
          {
            element: '#tour-modules-nav',
            popover: {
              title: 'Vos 4 outils principaux',
              description:
                "Qualyse a 4 modules. Chacun fait une chose précise — et vous pouvez les utiliser dans l'ordre que vous voulez.",
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: '#tour-module-01',
            popover: {
              title: "📋 Vous répondez à un appel d'offres ?",
              description:
                'Déposez votre TDR (document de référence) et Qualyse génère automatiquement votre proposition technique complète.' +
                '<div class="qts-tip">💡 Votre proposition est prête en quelques minutes.</div>',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '#tour-module-02',
            popover: {
              title: "🎙 Vous avez des enregistrements d'entretiens ?",
              description:
                'Déposez vos fichiers audio ou vidéo. Qualyse les transcrit et corrige automatiquement en quelques minutes.' +
                '<div class="qts-tip">💡 Formats supportés : MP3, MP4, M4A, WAV et plus.</div>',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '#tour-module-03',
            popover: {
              title: '🏷 Organisez et codifiez vos données qualitatives',
              description:
                "Uploadez jusqu'à 15 entretiens simultanément. Qualyse analyse l'ensemble du corpus et produit automatiquement un fichier Excel structuré — vos données classées par thématiques, avec citations représentatives et fréquences." +
                '<div class="qts-tip">💡 Vous choisissez l\'approche : inductive, déductive ou mixte.</div>',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '#tour-module-04',
            popover: {
              title: '📊 Vous voulez un rapport complet ?',
              description:
                "Importez vos données et choisissez votre méthode d'analyse. Qualyse produit un rapport structuré prêt à livrer." +
                '<div class="qts-tip">💡 Export Word inclus, prêt à soumettre.</div>',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '#tour-credits',
            popover: {
              title: '💳 Vos crédits gratuits',
              description:
                "Vous avez 100 crédits offerts pour tester Qualyse. Chaque utilisation consomme quelques crédits. Pas de surprise !",
              side: 'bottom',
              align: 'end',
            },
          },
          {
            element: '#tour-chat',
            popover: {
              title: "💬 Besoin d'aide ?",
              description:
                'Notre assistant est disponible à tout moment pour répondre à vos questions. Cliquez ici pour nous écrire.',
              side: 'top',
              align: 'end',
            },
          },
          {
            popover: {
              title: 'Vous êtes prêt ! 🚀',
              description:
                "Commencez par le module qui correspond à votre besoin du moment. Bonne analyse !",
            },
          },
        ],
      })

      destroyRef.fn = () => driverObj.destroy()
      driverObj.drive()
    }

    run()
  }, [])

  return (
    <style>{`
      /* ── Bulle principale ────────────────────────────────────── */
      .driver-popover.qualyse-tour {
        background: #ffffff !important;
        border: 1px solid #e0e0e0 !important;
        border-radius: 16px !important;
        max-width: 380px !important;
        padding: 24px !important;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12) !important;
        font-family: Arial, sans-serif !important;
      }

      /* ── Header injecté : badge + label ─────────────────────── */
      .qts-header {
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
        margin-bottom: 14px !important;
      }
      .qts-badge {
        width: 32px !important;
        height: 32px !important;
        border-radius: 50% !important;
        background: #f0f8e8 !important;
        border: 1px solid #d4edb8 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 15px !important;
        flex-shrink: 0 !important;
      }
      .qts-label {
        font-size: 10px !important;
        font-weight: 700 !important;
        letter-spacing: 0.1em !important;
        text-transform: uppercase !important;
        color: #2d6614 !important;
      }

      /* ── Titre principal ─────────────────────────────────────── */
      .qualyse-tour .driver-popover-title {
        color: #1a1a1a !important;
        font-size: 20px !important;
        font-weight: 700 !important;
        line-height: 1.3 !important;
        margin-bottom: 10px !important;
        font-family: Georgia, 'Times New Roman', serif !important;
      }

      /* ── Description ─────────────────────────────────────────── */
      .qualyse-tour .driver-popover-description {
        color: #444444 !important;
        font-size: 14px !important;
        line-height: 1.6 !important;
        margin: 0 !important;
      }

      /* ── Bloc astuce ─────────────────────────────────────────── */
      .qts-tip {
        background: #f0f8e8 !important;
        border: 1px solid #d4edb8 !important;
        border-radius: 8px !important;
        padding: 10px 12px !important;
        margin-top: 12px !important;
        color: #2d6614 !important;
        font-size: 13px !important;
        line-height: 1.5 !important;
      }

      /* ── Bouton fermer ───────────────────────────────────────── */
      .qualyse-tour .driver-popover-close-btn {
        color: #9ca3af !important;
        font-size: 18px !important;
        top: 16px !important;
        right: 16px !important;
        opacity: 1 !important;
        transition: color .15s !important;
      }
      .qualyse-tour .driver-popover-close-btn:hover {
        color: #1a1a1a !important;
      }

      /* ── Boutons navigation ──────────────────────────────────── */
      .qualyse-tour .driver-popover-footer {
        margin-top: 20px !important;
        gap: 8px !important;
      }
      .qualyse-tour .driver-popover-prev-btn {
        background: #ffffff !important;
        color: #6b7280 !important;
        border: 1px solid #d0d0d0 !important;
        border-radius: 8px !important;
        padding: 9px 16px !important;
        font-size: 13px !important;
        font-weight: 500 !important;
        cursor: pointer !important;
        transition: all .15s !important;
      }
      .qualyse-tour .driver-popover-prev-btn:hover {
        border-color: #9ca3af !important;
        color: #374151 !important;
      }
      .qualyse-tour .driver-popover-next-btn {
        background: #2d6614 !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 9px 20px !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        cursor: pointer !important;
        transition: background .15s !important;
        box-shadow: 0 2px 8px rgba(45,102,20,0.25) !important;
      }
      .qualyse-tour .driver-popover-next-btn:hover {
        background: #3a7a1a !important;
      }

      /* ── Pied injecté : tirets + étape + passer ─────────────── */
      .qts-foot {
        margin-top: 16px !important;
        border-top: 1px solid #f0f0f0 !important;
        padding-top: 14px !important;
      }
      .qts-prog {
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
        margin-bottom: 10px !important;
      }
      .qts-dashes {
        display: flex !important;
        gap: 4px !important;
        flex: 1 !important;
      }
      .qts-dash {
        height: 4px !important;
        flex: 1 !important;
        border-radius: 2px !important;
        background: #e0e0e0 !important;
        transition: background .2s !important;
      }
      .qts-dash.on {
        background: #2d6614 !important;
      }
      .qts-step {
        font-size: 11px !important;
        font-weight: 700 !important;
        color: #2d6614 !important;
        white-space: nowrap !important;
        letter-spacing: 0.03em !important;
      }
      .qts-skip {
        display: block !important;
        text-align: center !important;
        font-size: 12px !important;
        color: #9ca3af !important;
        text-decoration: none !important;
        cursor: pointer !important;
        transition: color .15s !important;
      }
      .qts-skip:hover {
        color: #6b7280 !important;
        text-decoration: underline !important;
      }

      /* ── Flèche (blanche pour bulle blanche) ─────────────────── */
      .qualyse-tour.driver-popover-arrow-side-top::before    { border-bottom-color: #ffffff !important; }
      .qualyse-tour.driver-popover-arrow-side-bottom::before { border-top-color: #ffffff !important; }
      .qualyse-tour.driver-popover-arrow-side-left::before   { border-right-color: #ffffff !important; }
      .qualyse-tour.driver-popover-arrow-side-right::before  { border-left-color: #ffffff !important; }

      /* ── Contour vert sur l'élément mis en surbrillance ─────── */
      .driver-active-element {
        outline: 2px solid #2d6614 !important;
        outline-offset: 3px !important;
        border-radius: 6px !important;
      }
    `}</style>
  )
}
